import axios from "axios";
import { backendUrlFor } from "./apiOrigin";

const BACKEND_URL = backendUrlFor(
  typeof window !== "undefined" ? window.location.hostname : "",
  process.env.REACT_APP_BACKEND_URL
);
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "nadia_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && getToken() && err.config?.url?.includes("/auth/me")) setToken(null);
    return Promise.reject(err);
  }
);
export const authHeaders = () => (getToken() ? { Authorization: `Bearer ${getToken()}` } : {});

// Auth (AuthService — JWT Bearer, email MOCK)
export const authRegister = (payload) => api.post("/auth/register", payload).then((r) => r.data);
export const authLogin = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data).catch(() => ({}));
export const authForgot = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const authReset = (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const authResendVerification = () => api.post("/auth/resend-verification").then((r) => r.data);
export const authVerifyMock = () => api.post("/auth/verify-email/mock").then((r) => r.data);
export const authVerifyEmail = (token) => api.post(`/auth/verify-email?token=${encodeURIComponent(token)}`).then((r) => r.data);
export const formatApiError = (e, fallback = "Qualcosa è andato storto. Riprova.") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || String(d);
};

// Business
export const getBusiness = () => api.get("/business").then((r) => r.data);
export const updateBusiness = (patch) => api.patch("/business", patch).then((r) => r.data);
// Long AI tasks return {job_id}: poll /jobs/{id} until done (ingress timeout is 60s). Errors are re-thrown axios-style.
export const awaitJob = async (data, { interval = 2500, timeout = 300000 } = {}) => {
  if (!data?.job_id) return data;
  const t0 = Date.now();
  while (Date.now() - t0 < timeout) {
    await new Promise((r) => setTimeout(r, interval));
    const job = await api.get(`/jobs/${data.job_id}`).then((r) => r.data);
    if (job.status === "done") return job.result;
    if (job.status === "error") { const e = new Error(job.error || "Operazione non riuscita"); e.response = { status: job.code || 503, data: { detail: job.error } }; throw e; }
  }
  throw new Error("Nadia sta impiegando più tempo del previsto: riprova tra poco");
};
export const analyzeUrl = (url, op_id) =>
  api.post(`/business/analyze${op_id ? `?op_id=${op_id}` : ""}`, { url }).then((r) => awaitJob(r.data));

// Demo gratuita + Billing (Crediti Nadia)
export const demoAnalyze = (url) => api.post("/demo/analyze", { url }).then((r) => r.data);
export const getDemoCampaignForecast = (url, daily_budget, currency) => api.post("/demo/campaign-forecast", { url, daily_budget, currency }, { timeout: 55000 }).then((r) => r.data);

export const getDemoVideo = (job_id) => api.get("/demo/video", { params: { job_id }, timeout: 15000 }).then((r) => r.data);
export const retryDemoVideo = (job_id) => api.post("/demo/video", null, { params: { job_id } }).then((r) => r.data);
export const getBillingConfig = () => api.get("/billing/config").then((r) => r.data);
export const getWallet = () => api.get("/billing/wallet").then((r) => r.data);
export const quoteCredits = (action) => api.post("/billing/quote", { action }).then((r) => r.data);
// Stripe Checkout (server-verified): kind = subscription | credit_pack
export const startCheckout = (kind, item_id) =>
  api.post("/billing/checkout", { kind, item_id, origin_url: window.location.origin }).then((r) => r.data);
export const getPaymentStatus = (session_id) => api.get(`/billing/payments/${session_id}/status`).then((r) => r.data);
export const getBillingProfile = () => api.get("/billing/profile").then((r) => r.data);
export const saveBillingProfile = (profile) => api.put("/billing/profile", profile).then((r) => r.data);
export const getInvoices = () => api.get("/billing/invoices").then((r) => r.data);
export const getPayments = () => api.get("/billing/payments").then((r) => r.data);
export const cancelSubscription = () => api.post("/billing/cancel").then((r) => r.data);
export const openBillingPortal = () => api.post("/billing/portal", { return_url: `${window.location.origin}/app/impostazioni` }).then((r) => r.data);
export const getServicesStatus = () => api.get("/integrations/services").then((r) => r.data);
export const getVideoJob = () => api.get("/video/jobs/current").then((r) => r.data);
/** Starts a purchase: asks for billing data first if missing, then redirects to Stripe. */
export const goToCheckout = async (nav, kind, item_id) => {
  try {
    const r = await startCheckout(kind, item_id);
    import("@/lib/tracking").then((t) => t.trackInitiateCheckout(kind, item_id)).catch(() => {});
    window.location.href = r.checkout_url;
  } catch (e) {
    if (e?.response?.status === 428) return nav(`/dati-fatturazione?kind=${kind}&item=${item_id}`);
    throw e;
  }
};
export const getVideoCost = () => api.get("/video/cost").then((r) => r.data);

// Goals
export const getGoals = () => api.get("/goals").then((r) => r.data);
export const createGoal = (payload) => api.post("/goals", payload).then((r) => r.data);

// Plan
export const getPlan = () => api.get("/marketing-plan").then((r) => r.data);
export const generatePlan = (body) => api.post("/marketing-plan/generate", body || {}).then((r) => awaitJob(r.data));
export const getGoalSuggestions = () => api.get("/goals/suggestions").then((r) => r.data);
export const patchPlan = (patch) => api.patch("/marketing-plan", patch).then((r) => r.data);
export const approvePlan = () => api.post("/marketing-plan/approve").then((r) => r.data);

// Creatives
export const generateCreatives = () =>
  api.post("/creatives/generate").then((r) => awaitJob(r.data));
export const getCreatives = () => api.get("/creatives").then((r) => r.data);
export const patchCreatives = (patch) =>
  api.patch("/creatives", patch).then((r) => r.data);
export const publishCreatives = () =>
  api.post("/creatives/publish").then((r) => r.data);

// Landings
export const generateLanding = (op_id) =>
  api.post(`/landings/generate${op_id ? `?op_id=${op_id}` : ""}`).then((r) => r.data);
export const getLanding = () => api.get("/landings/current").then((r) => r.data);
export const patchLanding = (patch) =>
  api.patch("/landings/current", patch).then((r) => r.data);
export const publishLanding = () =>
  api.post("/landings/publish").then((r) => r.data);
export const getPublicLanding = (slug) =>
  api.get(`/landings/public/${slug}`).then((r) => r.data);

// A/B variants
export const getVariants = () => api.get("/landings/variants").then((r) => r.data);
export const createVariantB = () => api.post("/landings/variants").then((r) => r.data);
export const setTrafficSplit = (split_a) =>
  api.patch("/landings/traffic-split", { split_a }).then((r) => r.data);
export const promoteVariant = (v) =>
  api.post(`/landings/promote/${v}`).then((r) => r.data);
export const patchLandingById = (id, patch) =>
  api.patch(`/landings/${id}`, patch).then((r) => r.data);

// Landing analytics + funnel
export const trackLanding = (payload) =>
  api.post("/landings/track", payload).then((r) => r.data);
export const getLandingStats = () =>
  api.get("/landings/stats").then((r) => r.data);
export const getFunnelSummary = () =>
  api.get("/funnel/summary").then((r) => r.data);

// Conversions
export const getConversionAdapters = () =>
  api.get("/conversion-adapters").then((r) => r.data);
export const setConversionConfig = (payload) =>
  api.post("/landings/conversion-config", payload).then((r) => r.data);

// Domain
export const setDomain = (payload) =>
  api.post("/landings/domain", payload).then((r) => r.data);

// Adapter test
export const testAdapter = (id, payload = {}) =>
  api.post(`/adapters/${id}/test`, payload).then((r) => r.data);

// Notifications
export const listNotifications = (unreadOnly = false) =>
  api.get(`/notifications${unreadOnly ? "?unread_only=true" : ""}`).then((r) => r.data);
export const readNotification = (id) =>
  api.post(`/notifications/${id}/read`).then((r) => r.data);
export const readAllNotifications = () =>
  api.post(`/notifications/read-all`).then((r) => r.data);
export const updateLeadPrefs = (prefs) =>
  api.patch("/settings/lead-prefs", prefs).then((r) => r.data);

// Leads + follow-ups
export const listLeads = (status) =>
  api.get(`/leads${status ? `?status=${status}` : ""}`).then((r) => r.data);
export const getLead = (id) => api.get(`/leads/${id}`).then((r) => r.data);
export const setLeadStatus = (id, status, note) =>
  api.patch(`/leads/${id}/status`, { status, note }).then((r) => r.data);
export const checkStaleLeads = () => api.post("/leads/check-stale").then((r) => r.data);
export const listFollowUps = (status) =>
  api.get(`/follow-ups${status ? `?status=${status}` : ""}`).then((r) => r.data);
export const editFollowUp = (id, text) =>
  api.patch(`/follow-ups/${id}`, { text }).then((r) => r.data);
export const approveFollowUp = (id, text, opId) =>
  api.post(`/follow-ups/${id}/approve`, { text }, { headers: { "X-Nadia-Operation-ID": opId || crypto.randomUUID() } }).then((r) => r.data);
export const rejectFollowUp = (id) =>
  api.post(`/follow-ups/${id}/reject`).then((r) => r.data);

// Video / Reel Studio (adapter MOCK)
export const getVideoStatus = () => api.get("/video/status").then((r) => r.data);
export const generateVideo = (payload) => api.post("/video/generate", payload).then((r) => r.data);
export const getVideo = () => api.get("/video/current").then((r) => r.data);
export const patchVideo = (patch) => api.patch("/video/current", patch).then((r) => r.data);
export const approveVideo = (op_id) => api.post(`/video/approve${op_id ? `?op_id=${op_id}` : ""}`).then((r) => r.data);
export const linkVideoToCampaign = () => api.post("/video/use-in-campaign").then((r) => r.data);

// Client media (chunked upload → persistent backend storage)
export const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
export const listMedia = () => api.get("/media").then((r) => r.data);
export const deleteMedia = (id) => api.delete(`/media/${id}`).then((r) => r.data);
export const reorderMedia = (ids) => api.patch("/media/reorder", { ids }).then((r) => r.data);
export const patchMedia = (id, patch) => api.patch(`/media/${id}`, patch).then((r) => r.data);
export const uploadMedia = async (file, onProgress) => {
  const CHUNK = 4 * 1024 * 1024;
  const { upload_id } = await api
    .post("/media/upload/init", { filename: file.name, size: file.size })
    .then((r) => r.data);
  const total = Math.ceil(file.size / CHUNK) || 1;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", upload_id);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
    await fetch(`${API}/media/upload/chunk`, { method: "POST", body: fd, headers: authHeaders() }).then((r) => {
      if (!r.ok) throw new Error("chunk failed");
    });
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  return api.post("/media/upload/complete", { upload_id, filename: file.name }).then((r) => r.data);
};

// Meta Pixel / CAPI (prepared integration)
export const getMetaTracking = () => api.get("/tracking/meta").then((r) => r.data);
export const testCapiEvent = (event_name = "Lead") =>
  api.post("/tracking/meta/test", { event_name }).then((r) => r.data);

// Tracking + Retargeting
export const getTrackingStatus = () =>
  api.get("/integrations/tracking-status").then((r) => r.data);
export const analyzeRetargeting = () =>
  api.post("/retargeting/analyze").then((r) => r.data);
export const buildRetargetingAudience = () =>
  api.post("/retargeting/audience").then((r) => r.data);
export const buildRetargetingCampaign = (op_id) =>
  api.post(`/retargeting/campaign${op_id ? `?op_id=${op_id}` : ""}`).then((r) => r.data);
export const getRetargetingCampaign = () =>
  api.get("/retargeting/campaign").then((r) => r.data);
export const approveRetargeting = () =>
  api.post("/retargeting/campaign/approve").then((r) => r.data);

// Campaigns
export const listCampaigns = () => api.get("/campaigns").then((r) => r.data);

// Actions
export const listActions = () => api.get("/actions").then((r) => r.data);
export const decideAction = (id, decision) =>
  api.post(`/actions/${id}/decide`, { decision }).then((r) => r.data);
export const generateAction = () => api.post("/actions/generate").then((r) => r.data);

// Timeline
export const getTimeline = () => api.get("/timeline").then((r) => r.data);

// Channels
export const getChannels = () => api.get("/channels").then((r) => r.data);

// Settings
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const updateAutonomy = (channel, level) =>
  api.patch("/settings/autonomy", { channel, level }).then((r) => r.data);

// Nadia Chat
export const sendChat = (session_id, message) =>
  api.post("/nadia/chat?sync=false", { session_id, message }, { headers: { "X-Nadia-Operation-ID": crypto.randomUUID() } }).then((r) => awaitJob(r.data, { interval: 1500 }));
export const getChatHistory = (session_id) =>
  api.get(`/nadia/chat/${session_id}`).then((r) => r.data);

// Dashboard
export const getDashboardSummary = () =>
  api.get("/dashboard/summary").then((r) => r.data);

// Integrations
export const getIntegrationsStatus = () =>
  api.get("/integrations/status").then((r) => r.data);

// Nadia brain
export const runOptimizer = () => api.post("/nadia/optimize").then((r) => r.data);
export const getOptimizerLatest = () => api.get("/nadia/optimize/latest").then((r) => r.data);

// Admin (solo ADMIN_EMAIL)
export const adminGetIntegrations = () => api.get("/admin/integrations").then((r) => r.data);
export const adminSmtpTest = (to) => api.post("/admin/integrations/smtp/test", { to: to || null }).then((r) => r.data);
export const adminGetIssuer = () => api.get("/admin/invoicing/issuer").then((r) => r.data);
export const adminSaveIssuer = (data) => api.put("/admin/invoicing/issuer", data).then((r) => r.data);
export const adminGetDocuments = (status) => api.get("/admin/invoicing/documents", { params: status ? { status } : {} }).then((r) => r.data);
export const adminGetDocumentXml = (id) => api.get(`/admin/invoicing/documents/${id}/xml`).then((r) => r.data);
export const adminProcessInvoicing = () => api.post("/admin/invoicing/process").then((r) => r.data);
export const adminRetryDocument = (id) => api.post(`/admin/invoicing/documents/${id}/retry`).then((r) => r.data);
export const syncMeta = (campaignId) =>
  api.post(`/campaigns/${campaignId}/sync-meta`).then((r) => r.data);
export const getWhatsAppCohort = () =>
  api.get("/whatsapp/cohort").then((r) => r.data);
export const sendWhatsAppRecall = (payload = {}, opId) =>
  api.post("/whatsapp/recall", payload, { headers: { "X-Nadia-Operation-ID": opId || crypto.randomUUID() } }).then((r) => r.data);
export const generateWeeklyReport = (send, to_email) =>
  api.post("/report/weekly", { send, to_email }).then((r) => r.data);

// Voice
export const ttsUrl = (text, voice = "nova") => `${API}/nadia/tts`;
export const fetchTtsBlob = async (text, voice = "nova") => {
  const res = await fetch(`${API}/nadia/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ text, voice }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Voce non disponibile"); }
  window.dispatchEvent(new Event("nadia-wallet-refresh"));
  return res.blob();
};
export const transcribeAudio = async (blob) => {
  const fd = new FormData();
  fd.append("audio", blob, "voice.webm");
  const res = await fetch(`${API}/nadia/stt`, { method: "POST", body: fd, headers: authHeaders() });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Trascrizione non disponibile"); }
  window.dispatchEvent(new Event("nadia-wallet-refresh"));
  return res.json();
};

// Where to plug future WordPress REST integration:
// Replace this baseURL with a WordPress site URL and expose /wp-json/nadia/v1/*
// endpoints that mirror the shape of these API calls.

// Ads multi-canale (Meta / TikTok / Google) — OAuth server-side, approvazione esplicita
export const getAdConnections = () => api.get("/ads/connections").then((r) => r.data);
export const adsOauthStart = (provider) => api.get(`/ads/${provider}/oauth/start`).then((r) => r.data);
export const adsSelectAccount = (provider, account_id, page_id) => api.post(`/ads/${provider}/select-account`, { account_id, page_id }).then((r) => r.data);
export const adsDisconnect = (provider) => api.post(`/ads/${provider}/disconnect`).then((r) => r.data);
export const adsPrepare = (body) => api.post("/ads/campaigns/prepare", body || {}).then((r) => r.data);
export const adsUnified = () => api.get("/ads/campaigns").then((r) => r.data);
export const adsPatchDraft = (id, patch) => api.patch(`/ads/campaigns/${id}`, patch).then((r) => r.data);
export const adsApprove = (id, paused = false, review_token = null) => api.post(`/ads/campaigns/${id}/approve`, { paused, review_token }).then((r) => r.data);
export const adsRemoteCampaigns = (provider) => api.get(`/ads/${provider}/campaigns/remote`).then((r) => r.data);
export const adsAppCheck = () => api.get("/ads/app-check").then((r) => r.data);
export const adsRefreshMetrics = () => api.post("/ads/metrics/refresh").then((r) => r.data);
export const adsPropose = () => api.post("/ads/recommendations/propose").then((r) => r.data);
export const adsDecide = (id, approve) => api.post(`/ads/recommendations/${id}/decide`, { approve }).then((r) => r.data);

export const adsReview = (id) => api.get(`/ads/campaigns/${encodeURIComponent(id)}/review`).then(r => r.data);
export const adsFromStrategy = (id) => api.post(`/ads/campaigns/from-strategy/${encodeURIComponent(id)}`).then(r => r.data);
export const adsOpenAIStatus = () => api.get("/ads/openai/status").then(r => r.data);

export const adsVerifyDisplayAsset = (id) => api.post(`/ads/campaigns/${encodeURIComponent(id)}/verify-display-asset`).then(r => r.data);
